import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import logo from '../wisdom.png';
import InventoryMiniDashboard from './InventoryMiniDashboard'; // Import the MiniDashboard component

const InventoryGrid = () => {
    const [inventory, setInventory] = useState([]);
    const [sort, setSort] = useState({ field: '', order: '' });
    const [clientCodeFilter, setClientCodeFilter] = useState([]);
    const [productCodeFilter, setProductCodeFilter] = useState([]);
    const [descriptionFilter, setDescriptionFilter] = useState([]);

    useEffect(() => {
        fetchInventory();
    }, []);

    const fetchInventory = async () => {
        try {
            const response = await axios.get('https://api.spherewms.com/v2/wms/wisdom-s/inventory', {
                headers: {
                    Authorization: 'Bearer edfcdfb396fca8cd94fba87c8c3592a1',
                },
            });
            const formattedInventory = formatResponseData(response.data);
            setInventory(formattedInventory);
        } catch (error) {
            console.error("Failed to fetch inventory:", error);
        }
    };

    const formatResponseData = (data) => {
        let formattedData = [];
        Object.entries(data).forEach(([clientCode, products]) => {
            Object.values(products).forEach((product) => {
                formattedData.push({ clientCode, ...product });
            });
        });
        return formattedData;
    };

    const handleSortChange = (field) => {
        const isAsc = sort.field === field && sort.order === 'asc';
        setSort({
            field: field,
            order: isAsc ? 'desc' : 'asc',
        });
    };

    const getUniqueOptions = (key) => {
        const unique = new Set(inventory.map(item => item[key]));
        return Array.from(unique).map(value => ({ value, label: value }));
    };

    const filteredInventory = inventory.filter(item => {
        const clientCodeMatch = clientCodeFilter.length ? clientCodeFilter.some(filter => filter.value === item.clientCode) : true;
        const productCodeMatch = productCodeFilter.length ? productCodeFilter.some(filter => filter.value === item.product_code) : true;
        const descriptionMatch = descriptionFilter.length ? descriptionFilter.some(filter => filter.value === item.description_1) : true;
        return clientCodeMatch && productCodeMatch && descriptionMatch;
    }).sort((a, b) => {
        if (sort.field && sort.order) {
            if (a[sort.field] < b[sort.field]) return sort.order === 'asc' ? -1 : 1;
            if (a[sort.field] > b[sort.field]) return sort.order === 'asc' ? 1 : -1;
        }
        return 0;
    });

    const formatNumber = (number) => number.toLocaleString('en-US');

    return (
        <div className="bg-gray-100 min-h-screen">
            <nav className="w-full flex justify-between items-center px-6 py-4 bg-white shadow z-10">
                <div className="flex items-center">
                    <img src={logo} alt="Company Logo" className="h-12" />
                </div>
                <div className="text-right">
                    <span className="text-xl font-semibold text-gray-700">Inventory Report</span>
                </div>
            </nav>
            {/* Adding mt-16 to accommodate the header height */}
            <div className="container mx-auto mt-16">
                <div className="mb-6 mt-4 px-4"> {/* Adjusted margin and padding */}
                    <div className="flex gap-4 mb-6"> {/* Increased bottom margin */}
                        <Select
                            isMulti
                            name="clientCodes"
                            options={getUniqueOptions('clientCode')}
                            className="flex-grow basic-multi-select"
                            classNamePrefix="select"
                            placeholder="Filter by Client Code"
                            onChange={setClientCodeFilter}
                        />
                        <Select
                            isMulti
                            name="productCodes"
                            options={getUniqueOptions('product_code')}
                            className="flex-grow basic-multi-select"
                            classNamePrefix="select"
                            placeholder="Filter by Product Code"
                            onChange={setProductCodeFilter}
                        />
                        <Select
                            isMulti
                            name="descriptions"
                            options={getUniqueOptions('description_1')}
                            className="flex-grow basic-multi-select"
                            classNamePrefix="select"
                            placeholder="Filter by Description"
                            onChange={setDescriptionFilter}
                        />
                    </div>
                    <InventoryMiniDashboard inventory={filteredInventory} />
                </div>
                <div className="overflow-x-auto px-4"> {/* Added padding */}
                    <div className="inline-block min-w-full">
                        <div className="border-collapse border border-gray-200">
                            <div className="bg-gray-200 flex text-center text-sm font-semibold text-gray-600 cursor-pointer">
                                <div className="w-1/6 px-4 py-2" onClick={() => handleSortChange('clientCode')}>Client Code</div>
                                <div className="w-1/6 px-4 py-2" onClick={() => handleSortChange('product_code')}>Product Code</div>
                                <div className="w-1/4 px-4 py-2" onClick={() => handleSortChange('description_1')}>Description</div>
                                <div className="w-1/6 px-4 py-2" onClick={() => handleSortChange('total_on_hand')}>Total On Hand</div>
                                <div className="w-1/6 px-4 py-2" onClick={() => handleSortChange('total_available')}>Available</div>
                                <div className="w-1/6 px-4 py-2" onClick={() => handleSortChange('total_expected')}>Expected</div>
                                <div className="w-1/6 px-4 py-2" onClick={() => handleSortChange('total_ordered')}>Ordered</div>
                                <div className="w-1/6 px-4 py-2" onClick={() => handleSortChange('on_purchase')}>On Purchase</div>
                                <div className="w-1/6 px-4 py-2" onClick={() => handleSortChange('on_order')}>On Order</div>
                                <div className="w-1/6 px-4 py-2" onClick={() => handleSortChange('on_build')}>On Build</div>
                                <div className="w-1/6 px-4 py-2" onClick={() => handleSortChange('on_transfer')}>On Transfer</div>

                            </div>
                            {filteredInventory.map((item, index) => (
                                <div key={index} className={`flex text-center items-center text-sm ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b border-gray-200`}>
                                    <div className="w-1/6 px-4 py-2">{item.clientCode}</div>
                                    <div className="w-1/6 px-4 py-2">{item.product_code}</div>
                                    <div className="w-1/4 px-4 py-2">{item.description_1}</div>
                                    <div className="w-1/6 px-4 py-2">{formatNumber(item.total_on_hand)}</div>
                                    <div className="w-1/6 px-4 py-2">{formatNumber(item.total_available)}</div>
                                    <div className="w-1/6 px-4 py-2">{formatNumber(item.total_expected)}</div>
                                    <div className="w-1/6 px-4 py-2">{formatNumber(item.total_ordered)}</div>
                                    <div className="w-1/6 px-4 py-2">{formatNumber(item.on_purchase)}</div>
                                    <div className="w-1/6 px-4 py-2">{formatNumber(item.on_order)}</div>
                                    <div className="w-1/6 px-4 py-2">{formatNumber(item.on_build)}</div>
                                    <div className="w-1/6 px-4 py-2">{formatNumber(item.on_transfer)}</div>

                                </div>
                            ))}                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InventoryGrid;
